import {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type ChangeEvent,
  type MouseEvent,
  type SyntheticEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Paper,
  Divider,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Box,
  TextField,
  Dialog,
  IconButton,
  Menu,
} from "@mui/material";
// import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  PanoramaFishEye as PanoramaFishEyeIcon,
} from "@mui/icons-material";
import SnackbarCustom from "../../../../components/SnackBarCustom";
import PopoverCustom from "../../../../components/PopoverCustom";
import SalesSelect from "./SaleSelect";
import ReasonsClosinCategorySelect from "./ReasonClosingCategorySelect";
import FacilityDamageCategorySelect from "./FacilityDamageCategorySelect";
import { formatDate } from "../../../../utils/deteUtils";
import NotDamageSelect from "./RecoveryReport/Cards/DamageStatus/NotDamageSelect";
import SsRecoveryCheckSelect from "./RecoveryReport/Cards/DamageStatus/SsRecoveryCheckSelect";
import { getDisasterStatuses, getDisasterStatusUsers, updateDisasterStatus } from "../../../../api";
import type { DisasterStatusItem, UpdateDisasterStatusPayload } from "../../../../types/disaster";
import type { SnackbarData } from "../../../../components/SnackBarCustom";
import type {
  BooleanSelectChangedHandler,
  DisasterStatusFieldValue,
  DisasterStatusUsersMap,
  KeySelectChangedHandler,
  SearchResultHandle,
  SituationCheckColumn,
  SituationCheckSearchContents,
} from "./types";
import { deriveSaleStatus } from "./deriveSaleStatus";
import type { AxiosError } from "axios";

type SearchResultProps = {
  searchContent: SituationCheckSearchContents;
};

type KeyValueEntry = { key: number; value: string };

type UpdateResult = {
  success: boolean;
  status?: SnackbarData;
  error?: AxiosError;
};

type EditUserInfo = {
  name?: string;
  m_organizations?: Array<{
    department_name?: string;
    m_role?: { id?: number };
    parent_m_organization?: { department_name?: string };
  }>;
};

type SituationCheckDisasterStatus = Omit<
  DisasterStatusItem,
  "not_damage" | "recovery_status"
> & {
  id: number;
  not_damage?: boolean | string | null;
  recovery_status?: boolean | string | number | null;
  edit_user?: EditUserInfo;
  update_user?: { name?: string };
  site?: DisasterStatusItem["site"] & {
    m_branch?: { name?: string; [key: string]: unknown };
    m_customer?: { name?: string; [key: string]: unknown };
    m_division?: { [key: string]: unknown };
    users?: DisasterStatusUserRow[];
  };
  disaster?: { id?: number };
  sale_status?: number | null;
  today_sales?: boolean;
  ago1_sales?: boolean;
  ago2_sales?: boolean;
  ago3_sales?: boolean;
  latest_sales_at?: string;
  latest_alive_at?: string;
  sales_staff?: string;
  operator?: string;
  disaster_detail_id?: number;
  site_id?: number;
  disaster_detail_kml_id?: number;
  sale_detail_status?: number | string | null;
  facility_damage_category?: number | string | null;
  reasons_closing_category?: number | string | null;
  personal_damage?: boolean;
  property_damage?: boolean;
  facility_damage?: boolean;
  damage_details?: string | null;
  reasons_closing?: string | null;
};

type DisasterStatusUserRow = {
  name?: string;
  m_organizations?: Array<{
    department_name?: string;
    parent_m_organization?: { department_name?: string };
  }>;
};

//
const columns: SituationCheckColumn[] = [
  {
    id: "display_code",
    label: "Sコード",
    minWidth: "80px",
    isSmallScreen: true,
    changeColor: true,
    site: true,
    position: "sticky",
    left: 0,
    zIndex: 3,
  },
  {
    id: "name",
    label: "SS名称",
    minWidth: "112px",
    isSmallScreen: true,
    changeColor: true,
    site: true,
    position: "sticky",
    left: 80,
    zIndex: 3,
  },
  {
    id: "name",
    label: "支店",
    minWidth: "144px",
    isSmallScreen: false,
    mBranch: true,
  },
  {
    id: "target_user",
    label: "報告対象者",
    minWidth: "144px",
    isSmallScreen: false,
    targetUser: true,
  },
  {
    id: "edit_name",
    label: "報告者",
    minWidth: "144px",
    isSmallScreen: false,
    editUser: true,
  },
  {
    id: "operator",
    label: "運営店名",
    minWidth: "144px",
    isSmallScreen: false,
    site: true,
  },
  {
    id: "name",
    label: "特約店",
    minWidth: "144px",
    isSmallScreen: false,
    mCustomer: true,
  },
  {
    id: "sales_staff",
    label: "担当者名",
    minWidth: "144px",
    isSmallScreen: false,
  },
  {
    id: "pref_name",
    label: "都道府県名",
    minWidth: "144px",
    isSmallScreen: false,
    mDivision: true,
  },
  {
    id: "city_name",
    label: "市区町村名",
    minWidth: "144px",
    isSmallScreen: false,
    mDivision: true,
  },
  {
    id: "owned_kind",
    label: "所有区分",
    minWidth: "144px",
    isSmallScreen: false,
    site: true,
    defaultSelecter: true,
  },
  {
    id: "business_time",
    label: "営業時間",
    minWidth: "144px",
    isSmallScreen: false,
    site: true,
  },
  {
    id: "holiday",
    label: "定休日",
    minWidth: "144px",
    isSmallScreen: false,
    site: true,
  },
  {
    id: "sale_status",
    label: "営業状況",
    minWidth: "80px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "latest_sales_at",
    label: "最終売上日時",
    minWidth: "144px",
    isSmallScreen: false,
  },
  {
    id: "today_sales",
    label: "本日売上状況",
    minWidth: "112px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "ago1_sales",
    label: "1日前売上状況",
    minWidth: "112px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "ago2_sales",
    label: "2日前売上状況",
    minWidth: "112px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "ago3_sales",
    label: "3日前売上状況",
    minWidth: "112px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "damage_at",
    label: "被害確認日時",
    minWidth: "112px",
    isSmallScreen: false,
  },

  //以下11_disaster-status.mdでの処理範囲
  {
    id: "recovery_status",
    label: "点検記録簿（SS復旧チェック）実施で異常無し",
    minWidth: "144px",
    pullDown: true,
    isSmallScreen: false,
  },
  {
    id: "not_damage",
    label: "被害なし",
    minWidth: "144px",
    pullDown: true,
    isSmallScreen: false,
  },
  {
    id: "personal_damage",
    label: "人的被害",
    minWidth: "144px",
    checkBox: true,
    isSmallScreen: false,
  },
  {
    id: "property_damage",
    label: "第三者への物損",
    minWidth: "144px",
    checkBox: true,
    isSmallScreen: false,
  },
  {
    id: "facility_damage",
    label: "SS設備被害",
    minWidth: "144px",
    checkBox: true,
    isSmallScreen: false,
  },
  {
    id: "is_electric",
    label: "電話・インターネット接続不良有",
    minWidth: "144px",
    checkBox: true,
    isSmallScreen: false,
  },

  {
    id: "sale_detail_status",
    label: "営業状況",
    minWidth: "176px",
    pullDown: true,
    isSmallScreen: false,
  },
  // {
  //   id: "facility_damage_category",
  //   label: "SS施設被害分類",
  //   minWidth: "176px",
  //   pullDown: true,
  //   isSmallScreen: false,
  // },
  // {
  //   id: "reasons_closing_category",
  //   label: "主たる休業理由",
  //   minWidth: "176px",
  //   pullDown: true,
  //   isSmallScreen: false,
  // },
  {
    id: "damage_details",
    label: "被害詳細",
    minWidth: "144px",
    textField: true,
    isSmallScreen: false,
  },
  {
    id: "reasons_closing",
    label: "再開見込",
    minWidth: "144px",
    textField: true,
    isSmallScreen: false,
  },
  {
    id: "update_user",
    label: "最終記入者",
    minWidth: "144px",
    isSmallScreen: false,
  },
  {
    id: "edit_at",
    label: "最終記入日時",
    minWidth: "144px",
    isSmallScreen: false,
  },
  {
    id: "reportButton",
    label: "",
    minWidth: "112px",
    button: true,
    align: "right",
    isSmallScreen: true,
    position: "sticky",
    right: 0,
    zIndex: 3,
  },
];

const ownKind: KeyValueEntry[] = [
  {
    key: 1,
    value: "自己",
  },
  {
    key: 9,
    value: "社有",
  },
];

const compeKind: KeyValueEntry[] = [
  {
    key: 1,
    value: "自己",
  },
  {
    key: 9,
    value: "社有",
  },
];

const disaterKind: KeyValueEntry[] = [
  {
    key: 0,
    value: "区分1",
  },
  {
    key: 1,
    value: "区分2",
  },
  {
    key: 2,
    value: "区分3",
  },
];
const saleStatus: KeyValueEntry[] = [
  {
    key: 0,
    value: "休止",
  },
  {
    key: 1,
    value: "営業",
  },
];

//ファイル内連想配列使用
type DefaultSelectorProps = {
  column: string;
  value: SituationCheckDisasterStatus;
};

const DefaultSelector = ({ column, value }: DefaultSelectorProps) => {
  const useSelector: KeyValueEntry[] | null =
    column === "owned_kind"
      ? ownKind
      : column === "compe_kind"
        ? compeKind
        : column === "disaster_kind"
          ? disaterKind
          : column === "sale_status"
            ? saleStatus
            : null;

  const useKey =
    column === "sale_status"
      ? (value?.[column as keyof SituationCheckDisasterStatus] as
          | number
          | string
          | undefined)
      : (value?.site?.[column as keyof NonNullable<SituationCheckDisasterStatus["site"]>] as
          | number
          | string
          | undefined);

  function findValue(key: number | string | undefined) {
    if (!useSelector) return null;
    const found = useSelector.find((item) => item.key === key);
    return found ? found.value : null;
  }

  const outputValue = findValue(useKey);
  return <>{outputValue}</>;
};

//セレクター設定
type SelecterValidateProps = {
  disasterData: SituationCheckDisasterStatus;
  changedValue: (value: DisasterStatusFieldValue) => void;
  column: string;
};

const SelecterValidate = ({
  disasterData,
  changedValue,
  column,
}: SelecterValidateProps) => {
  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ pointerEvents: "auto" }}>
      {column === "recovery_status" ? (
        <SsRecoveryCheckSelect
          recoveryStatus={disasterData?.recovery_status}
          changedValue={(e) => changedValue(e)}
        />
      ) : column === "not_damage" ? (
        <NotDamageSelect
          notDamage={disasterData?.not_damage}
          changedValue={(e) => changedValue(e)}
        />
      ) : column === "sale_detail_status" ? (
        <SalesSelect
          saleDetailStatus={disasterData?.sale_detail_status}
          changedValue={(e) => changedValue(e)}
        />
      ) : column === "facility_damage_category" ? (
        <FacilityDamageCategorySelect
          facilityDamageCategory={disasterData?.facility_damage_category}
          changedValue={(e) => changedValue(e)}
        />
      ) : column === "reasons_closing_category" ? (
        <ReasonsClosinCategorySelect
          reasonsClosinCategory={disasterData?.reasons_closing_category}
          changedValue={(e) => changedValue(e)}
        />
      ) : null}
    </Box>
  );
};

//テキストフィールド設定
type StatusesTextfieldProps = {
  disasterData: SituationCheckDisasterStatus;
  changedValue: (value: string) => void;
  column: string;
};

const StatusesTextfield = ({
  disasterData,
  changedValue,
  column,
}: StatusesTextfieldProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (typeof changedValue === "function") {
      changedValue(e.target.value);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <TextField
        name={`${column}TextField`}
        size="small"
        variant="outlined"
        value={String(disasterData?.[column as keyof SituationCheckDisasterStatus] ?? "")}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ pointerEvents: "auto" }}
      />
    </Box>
  );
};

//チェックボックス設定
type CheckboxValidateProps = {
  disasterData: SituationCheckDisasterStatus;
  changedValue: (value: boolean) => void;
  column: string;
};

const CheckboxValidate = ({
  disasterData,
  changedValue,
  column,
}: CheckboxValidateProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    changedValue(e.target.checked);
  };

  return (
    <Checkbox
      name={`${column}Checkbox`}
      size="small"
      checked={Boolean(disasterData[column as keyof SituationCheckDisasterStatus])}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => handleChange(e)}
      sx={{ cursor: "pointer", pointerEvents: "auto" }}
    />
  );
};

//マルバツ設定
const existenceValidate = (input: boolean | undefined) =>
  input ? (
    <Box>
      <PanoramaFishEyeIcon />
    </Box>
  ) : (
    <Box>
      <CloseIcon />
    </Box>
  );

// 報告者カラム用
type EditUserColumnProps = {
  disasterStatus: SituationCheckDisasterStatus;
};

const EditUserColumn = ({ disasterStatus }: EditUserColumnProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={handleMenuClick}
        sx={{
          cursor: "pointer",
          pointerEvents: "auto",
          "&:hover": {
            opacity: 0.8,
            textDecoration: "underline",
          },
        }}
      >
        {disasterStatus?.edit_user?.name || ""}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              paddingX: "8px",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Table size="small">
            <TableHead
              sx={{ position: "sticky", top: 40, backgroundColor: "#fff", zIndex: 1 }}
            >
              <TableRow>
                <TableCell>会社名</TableCell>
                <TableCell>部署名</TableCell>
                <TableCell>氏名</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {disasterStatus?.edit_user?.m_organizations?.[0]?.parent_m_organization?.department_name || ""}
                </TableCell>
                <TableCell>
                  {disasterStatus?.edit_user?.m_organizations?.[0]?.department_name || ""}
                </TableCell>
                <TableCell>{disasterStatus?.edit_user?.name || ""}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={{ height: "16px", position: "sticky", bottom: 0 }} />
        </Box>
      </Menu>
    </>
  );
};

// 報告対象者カラム用
type TargetUserColumnProps = {
  disasterStatusId: number;
  disasterStatusUsers: DisasterStatusUsersMap;
};

const TargetUserColumn = ({
  disasterStatusId,
  disasterStatusUsers,
}: TargetUserColumnProps) => {
  const [anchorElRole7, setAnchorElRole7] = useState<HTMLElement | null>(null);
  const [anchorElRole8, setAnchorElRole8] = useState<HTMLElement | null>(null);
  const openRole7 = Boolean(anchorElRole7);
  const openRole8 = Boolean(anchorElRole8);

  // disaster_status_idに対応するユーザー情報を取得
  const userData = disasterStatusUsers.get(disasterStatusId) || { role7: [], role8: [] };
  const role7Users = userData.role7 || [];
  const role8Users = userData.role8 || [];

  // ユーザー件数が1件以上の場合に表示
  const showRole7 = role7Users.length >= 1;
  const showRole8 = role8Users.length >= 1;

  const handleMenuRole7 = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorElRole7(e.currentTarget);
  };

  const handleMenuRole8 = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorElRole8(e.currentTarget);
  };

  const handleCloseRole7 = (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAnchorElRole7(null);
  };

  const handleCloseRole8 = (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAnchorElRole8(null);
  };

  return (
    <Box sx={{ display: "grid", gap: "4px" }}>
      {showRole7 && (
        <>
          <Box
            onClick={handleMenuRole7}
            sx={{
              borderRadius: "20px",
              padding: "4px",
              width: "fit-content",
              minWidth: "60px",
              height: "fit-content",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              backgroundColor: "yellowgreen",
              cursor: "pointer",
              pointerEvents: "auto",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            特約店
          </Box>
          <Menu
            anchorEl={anchorElRole7}
            open={openRole7}
            onClose={() => handleCloseRole7()}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  paddingX: "8px",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                <IconButton
                  aria-label="close"
                  onClick={handleCloseRole7}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Table size="small">
                <TableHead
                  sx={{ position: "sticky", top: 40, backgroundColor: "#fff", zIndex: 1 }}
                >
                  <TableRow>
                    <TableCell>会社名</TableCell>
                    <TableCell>部署名</TableCell>
                    <TableCell>氏名</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {role7Users.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {user?.m_organizations?.[0]?.parent_m_organization?.department_name ||
                          ""}
                      </TableCell>
                      <TableCell>
                        {user?.m_organizations?.[0]?.department_name || ""}
                      </TableCell>
                      <TableCell>{user?.name || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ height: "16px", position: "sticky", bottom: 0 }} />
            </Box>
          </Menu>
        </>
      )}
      {showRole8 && (
        <>
          <Box
            onClick={handleMenuRole8}
            sx={{
              borderRadius: "20px",
              padding: "4px",
              width: "fit-content",
              minWidth: "60px",
              height: "fit-content",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              backgroundColor: "orange",
              cursor: "pointer",
              pointerEvents: "auto",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            SS店舗
          </Box>
          <Menu
            anchorEl={anchorElRole8}
            open={openRole8}
            onClose={() => handleCloseRole8()}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Box sx={{ paddingX: "8px", maxHeight: "500px", overflowY: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  paddingX: "8px",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                <IconButton
                  aria-label="close"
                  onClick={handleCloseRole8}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Table size="small">
                <TableHead
                  sx={{ position: "sticky", top: 40, backgroundColor: "#fff", zIndex: 1 }}
                >
                  <TableRow>
                    <TableCell>会社名</TableCell>
                    <TableCell>部署名</TableCell>
                    <TableCell>氏名</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {role8Users.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {user?.m_organizations?.[0]?.parent_m_organization?.department_name ||
                          ""}
                      </TableCell>
                      <TableCell>
                        {user?.m_organizations?.[0]?.department_name || ""}
                      </TableCell>
                      <TableCell>{user?.name || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ height: "16px", position: "sticky", bottom: 0 }} />
            </Box>
          </Menu>
        </>
      )}
    </Box>
  );
};

const SearchResult = forwardRef<SearchResultHandle, SearchResultProps>(
  ({ searchContent }, ref) => {
  //クエリパラメータ取得
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLElement>, path: string) => {
    event.stopPropagation();
    navigate(path, { state: { previousPage: "situationcheck" } });
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [disasterStatuses, setDisasterStatuses] = useState<
    SituationCheckDisasterStatus[]
  >([]);
  const [originalDisasterStatuses, setOriginalDisasterStatuses] = useState<
    SituationCheckDisasterStatus[]
  >([]);
  const [disasterStatusUsers, setDisasterStatusUsers] =
    useState<DisasterStatusUsersMap>(new Map());
  const [totalData, setTotalData] = useState<number>(0);

  const lastSearchedContentRef = useRef<SituationCheckSearchContents | null>(
    null,
  );

  const getDisasterSsData = useCallback(
    async (overridePage?: number) => {
    const pageToUse = overridePage !== undefined ? overridePage : page;
    // 検索ボタン押下時は現在の検索条件、ページネーション時は最後に確定した条件を使用
    const contentToUse =
      overridePage !== undefined
        ? searchContent
        : (lastSearchedContentRef.current ?? searchContent);
    console.log("getDisasterSsData");
    try {
      const displayId = contentToUse.display_id;
      if (!displayId) {
        return;
      }
      const response = await getDisasterStatuses(displayId, {
        display_code: contentToUse.display_code,
        name: null,
        pref: contentToUse.pref,
        m_branch_id: contentToUse.m_branch_id,
        m_customer_id: contentToUse.m_customer_id,
        latest_alive: contentToUse.latest_alive,
        latest_sales: contentToUse.latest_sales,
        latest_authori: contentToUse.latest_authori,
        is_input: contentToUse.is_input,
        is_damage: contentToUse.is_damage,
        order: null,
        sort: null,
        page: pageToUse + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      const data = Array.isArray(response.disaster_statuses)
        ? (response.disaster_statuses as SituationCheckDisasterStatus[])
        : [];
      const total = response.total_count ?? 0;
      // テナント情報をステートにセット
      setDisasterStatuses(data);
      // 元データを深いコピーで保存（変更追跡用）
      setOriginalDisasterStatuses(JSON.parse(JSON.stringify(data)));
      setTotalData(total);
      // 検索ボタンで1ページ目を指定した場合はページ状態を同期し、確定条件を更新
      if (overridePage !== undefined) {
        setPage(overridePage);
        lastSearchedContentRef.current = searchContent;
      } else {
        // 初回またはページネーション時は今回使用した条件を保持
        lastSearchedContentRef.current = contentToUse;
      }
      //スナックバーステート
      setSnackBarstatus(response);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  }, [searchContent, page, rowsPerPage]);

  // クエリの disaster-id が変わったあと SearchArea が display_id を同期したタイミングで一覧を取り直す
  const displayIdForSearch = searchContent?.display_id;
  const prevDisplayIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevDisplayIdRef.current === undefined) {
      prevDisplayIdRef.current = displayIdForSearch;
      return;
    }
    if (prevDisplayIdRef.current === displayIdForSearch) return;
    prevDisplayIdRef.current = displayIdForSearch;
    if (!displayIdForSearch) return;
    setPage(0);
    lastSearchedContentRef.current = null;
    getDisasterSsData(0);
  }, [displayIdForSearch, getDisasterSsData]);

  const getDisasterStatusUsersLength = useCallback(async () => {
    if (!Array.isArray(disasterStatuses) || disasterStatuses.length === 0 || !disasterId) {
      return;
    }

    const disasterStatusIds = disasterStatuses.map((status) => status.id);
    const disasterIdValue = disasterStatuses[0]?.disaster?.id;
    if (!disasterIdValue) {
      return;
    }

    const roleIds = [7, 8];
    const userDataMap: DisasterStatusUsersMap = new Map();

    // 各disaster_status_idに対して初期化
    disasterStatusIds.forEach((id) => {
      userDataMap.set(id, { role7: [], role8: [] });
    });

    // 各role_idに対してAPIを呼び出す
    const promises = roleIds.map(async (roleId) => {
      try {
        const response = await getDisasterStatusUsers(disasterId, {
          disaster_id: disasterIdValue,
          role_id: roleId,
          disaster_status_ids: disasterStatusIds,
        });
        if (response.status !== "ok") {
          return;
        }
        const statusUsers = (
          (response as { disaster_statuses?: SituationCheckDisasterStatus[] })
            .disaster_statuses ?? []
        );

        statusUsers.forEach((disasterStatus) => {
          const statusId = disasterStatus.id;
          const users = disasterStatus.site?.users || [];

          const entry = userDataMap.get(statusId);
          if (entry) {
            if (roleId === 7) {
              entry.role7 = users;
            } else if (roleId === 8) {
              entry.role8 = users;
            }
          }
        });
      } catch (error) {
        console.error(`API呼び出しエラー (role_id=${roleId}):`, error);
      }
    });

    // すべてのAPI呼び出しが完了したら状態を更新
    await Promise.all(promises);
    setDisasterStatusUsers(userDataMap);
  }, [disasterStatuses, disasterId]);

  type ButtonVaridateProps = {
    button?: boolean;
    id: number;
    rowNum: number;
    isDialog?: boolean;
    detailId?: number;
    siteId?: number;
    kmlId?: number;
  };

  const ButtonVaridate = ({
    button,
    id,
    rowNum,
    isDialog,
  }: ButtonVaridateProps) => {
    return button ? (
      <Box sx={{ display: "flex", gap: "8px", width: "max-content" }}>
        <IconButton
          name="saveButton"
          color="primary"
          sx={{
            pointerEvents: "auto",
            "@media screen and (max-width:900px)": {
              display: isDialog === true ? "table-cell" : "none",
            },
          }}
          onClick={(e) => registerButtonClick(e, rowNum)}
        >
          <SaveIcon />
        </IconButton>
        <Button
          name="reportButton"
          variant="contained"
          onClick={(event) =>
            handleClick(
              event,
              `/dashboard/situationcheck/report?disaster-id=${disasterId}&status-id=${id}`
            )
          }
          sx={{ pointerEvents: "auto" }}
        >
          被害状況報告
        </Button>
      </Box>
    ) : null;
  };

  //初回起動時の読み込み回避
  const isFirstRender = useRef(false);

  useEffect(() => {
    // このeffectは初回レンダー時のみ呼ばれるeffect
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      // 初回レンダー判定
      isFirstRender.current = false; // もう初回レンダーじゃないよ代入
      return; // 初回レンダー時はAPI呼び出しをスキップ
    }
    getDisasterSsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  // disasterStatusesが更新されたら、ユーザー情報を取得
  useEffect(() => {
    if (Array.isArray(disasterStatuses) && disasterStatuses.length > 0 && disasterId) {
      getDisasterStatusUsersLength();
    }
  }, [disasterStatuses, disasterId, getDisasterStatusUsersLength]);

  //登録ボタン選択行番号管理
  const [selectedRowNum, setSelectedRowNum] = useState<number | "">("");

  const handleChangeDisasterStatus = (
    inputValue: DisasterStatusFieldValue,
    statusId: number,
    column: string,
  ) => {
    console.log("inputValue, rowNum", inputValue, statusId);
    const updatedArray = [...disasterStatuses]; // オリジナルの配列をコピー
    //sites配列内からidが当てはまる配列を探す
    const updatedItem = updatedArray.find((item) => item.id === statusId);
    if (updatedItem) {
      (updatedItem as Record<string, DisasterStatusFieldValue>)[column] =
        inputValue;
      if (column === "sale_detail_status") {
        updatedItem.sale_status = deriveSaleStatus(
          inputValue as number | string | null | undefined
        );
      }
      console.log("updatedArray, column", updatedArray, column);
      setDisasterStatuses(updatedArray);
    } else {
      console.error(`sites with id ${statusId} not found`);
    }
  };

  //復旧報告登録（単一データ用）
  const putrecoberyReport = async (
    disasterStatus: SituationCheckDisasterStatus,
    _originalStatus: SituationCheckDisasterStatus | undefined,
    isSuccess: boolean,
  ): Promise<UpdateResult | undefined> => {
    if (!isSuccess || !disasterStatus) {
      return;
    }

    // どちらもnull/undefinedの場合はnullのまま

    const damageDetails =
      typeof disasterStatus.damage_details === "string"
        ? disasterStatus.damage_details
        : "";
    const reasonsClosing =
      typeof disasterStatus.reasons_closing === "string"
        ? disasterStatus.reasons_closing
        : "";

    const params: UpdateDisasterStatusPayload = {
      id: disasterStatus.id,
      disaster_id: Number(
        disasterStatus.disaster_id ?? disasterStatus.disaster?.id ?? 0,
      ),
      not_damage:
        disasterStatus.not_damage != null
          ? String(disasterStatus.not_damage)
          : "",
      personal_damage: Boolean(disasterStatus.personal_damage),
      property_damage: Boolean(disasterStatus.property_damage),
      facility_damage: Boolean(disasterStatus.facility_damage),
      is_electric: disasterStatus.is_electric ? "1" : "0",
      recovery_status:
        disasterStatus.recovery_status != null
          ? disasterStatus.recovery_status
            ? "1"
            : "0"
          : "",
      damage_details: damageDetails.match(/\S/g) ? damageDetails : null,
      sale_detail_status:
        disasterStatus.sale_detail_status != null
          ? String(disasterStatus.sale_detail_status)
          : null,
      sale_status: (() => {
        const saleStatus = deriveSaleStatus(disasterStatus.sale_detail_status);
        return saleStatus == null ? null : String(saleStatus);
      })(),
      reasons_closing: reasonsClosing.match(/\S/g) ? reasonsClosing : null,
      facility_damage_category:
        disasterStatus.facility_damage_category != null
          ? String(disasterStatus.facility_damage_category)
          : null,
      reasons_closing_category:
        disasterStatus.reasons_closing_category != null
          ? String(disasterStatus.reasons_closing_category)
          : null,
    };

    console.log("復旧報告入力データ", params);

    try {
      const status = await updateDisasterStatus(params);
      console.log("復旧報告レスポンス", status);
      return { success: true, status };
    } catch (error) {
      console.error("エラーが発生しました:", error);
      return { success: false, error: error as AxiosError };
    }
  };

  const putrecoberyReportSingle = async (isSuccess: boolean) => {
    if (
      !isSuccess ||
      selectedRowNum === "" ||
      disasterStatuses[selectedRowNum] === undefined
    ) {
      return;
    }
    const currentStatus = disasterStatuses[selectedRowNum];
    const originalStatus = originalDisasterStatuses.find(
      (orig) => orig.id === currentStatus.id
    );
    const result = await putrecoberyReport(currentStatus, originalStatus, true);
    if (result?.success) {
      setSnackBarstatus(result.status);
      setSnackbarOpen(true);
    } else if ((result?.error as AxiosError | undefined)?.response?.status === 403) {
      setSnackBarstatus({
        message: "権限が与えられていません",
        status: "error",
      });
      setSnackbarOpen(true);
    }
  };

  // 変更を検出する関数
  const hasChanged = (
    current: SituationCheckDisasterStatus,
    original: SituationCheckDisasterStatus | undefined,
  ) => {
    if (!original) return false;

    // 比較対象のフィールド
    const fieldsToCompare = [
      'not_damage',
      'personal_damage',
      'property_damage',
      'facility_damage',
      'is_electric',
      'recovery_status',
      'damage_details',
      'sale_detail_status',
      'reasons_closing',
      'facility_damage_category',
      'reasons_closing_category',
    ];

    for (const field of fieldsToCompare) {
      const currentValue = current[field as keyof SituationCheckDisasterStatus];
      const originalValue = original[field as keyof SituationCheckDisasterStatus];

      // 文字列の場合、空白のみの場合はnullとして扱う
      if (typeof currentValue === 'string' && !currentValue.match(/\S/g)) {
        if (originalValue !== null && originalValue !== undefined && originalValue !== '') {
          return true;
        }
      } else if (currentValue !== originalValue) {
        return true;
      }
    }

    return false;
  };

  // 一括登録ボタンクリック処理
  const [popoverBatchRegisterOpen, setPopoverBatchRegisterOpen] = useState(false);
  const [batchRegisterCount, setBatchRegisterCount] = useState(0);

  const handleBatchRegisterClick = () => {
    const changedStatuses: SituationCheckDisasterStatus[] = [];
    disasterStatuses.forEach((currentStatus, index) => {
      const originalStatus = originalDisasterStatuses.find(
        (orig) => orig.id === currentStatus.id
      );

      if (hasChanged(currentStatus, originalStatus)) {
        changedStatuses.push(currentStatus);
      }
    });

    if (changedStatuses.length === 0) {
      setSnackBarstatus({
        status: 'info',
        message: '変更されたデータがありません。',
      });
      setSnackbarOpen(true);
      return;
    }

    // 変更件数を保存してポップオーバーを表示
    setBatchRegisterCount(changedStatuses.length);
    setPopoverBatchRegisterOpen(true);
  };

  const batchRegisterPopupClose = () => {
    setPopoverBatchRegisterOpen(false);
  };

  // 一括登録関数（実際の登録処理）
  const batchRegister = async (isSuccess: boolean) => {
    if (!isSuccess) {
      return;
    }

    const changedStatuses: SituationCheckDisasterStatus[] = [];

    // 現在表示されているデータの中で変更があったものを検出
    disasterStatuses.forEach((currentStatus, index) => {
      const originalStatus = originalDisasterStatuses.find(
        (orig) => orig.id === currentStatus.id
      );

      if (hasChanged(currentStatus, originalStatus)) {
        changedStatuses.push(currentStatus);
      }
    });

    if (changedStatuses.length === 0) {
      setSnackBarstatus({
        status: 'info',
        message: '変更されたデータがありません。',
      });
      setSnackbarOpen(true);
      return;
    }

    // 変更があったデータを順次登録
    const results = [];
    for (const status of changedStatuses) {
      const originalStatus = originalDisasterStatuses.find(
        (orig) => orig.id === status.id
      );
      const result = await putrecoberyReport(status, originalStatus, true);
      results.push(result);
    }

    // 結果をまとめて表示
    const successCount = results.filter((r) => r?.success).length;
    console.log("successCount", successCount);
    console.log("results.length", results.length);
    const failCount = results.length - successCount;
    console.log("failCount", failCount);

    const has403 = results.some((r) => r?.error?.response?.status === 403);

    if (has403) {
      setSnackBarstatus({
        message: "権限が与えられていません",
        status: "error",
      });
      setSnackbarOpen(true);
    } else {
      // 全件成功の場合はsuccess、1件でも失敗した場合はwarning
      const snackbarStatus = failCount === 0 ? 'ok' : 'ng';
      const snackbarMessage = failCount === 0
        ? `${totalData}(全件数)件中${changedStatuses.length}件更新しました。`
        : `更新対象${changedStatuses.length}件中${successCount}件の更新に成功しました。${failCount}件の更新に失敗しました。`;

      setSnackBarstatus({
        status: snackbarStatus,
        message: snackbarMessage,
      });
      setSnackbarOpen(true);
    }

    // 登録成功後、元データを更新
    if (successCount > 0) {
      setOriginalDisasterStatuses(JSON.parse(JSON.stringify(disasterStatuses)));
    }

    // ポップオーバーを閉じる
    setPopoverBatchRegisterOpen(false);
  };

  // 親コンポーネントから呼び出せる関数を公開
  useImperativeHandle(ref, () => ({
    batchRegister: handleBatchRegisterClick,
  }));

  //登録ボタンクリック
  const [popoverRegisterOpen, setPopoverRegisterOpen] = useState(false);
  const registerButtonClick = (e: MouseEvent<HTMLElement>, rowNum: number) => {
    e.stopPropagation();
    setSelectedRowNum(rowNum);
    setPopoverRegisterOpen(true);
  };
  const registerPopupClose = () => {
    setPopoverRegisterOpen(false);
  };

  const [selectedRow, setSelectedRow] =
    useState<SituationCheckDisasterStatus | null>(null);
  //詳細ポップアップ設定
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (
    row: SituationCheckDisasterStatus,
    rowNum: number,
  ) => {
    setSelectedRow(row);
    setSelectedRowNum(rowNum);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  //検索結果ページネーション管理
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData | undefined>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // 被害なしヘッダーチェックボックス管理
  const [notDamageHeaderCheckboxChecked, setNotDamageHeaderCheckboxChecked] = useState(false);
  const [notDamageHeaderPopoverOpen, setNotDamageHeaderPopoverOpen] = useState(false);
  const [notDamageHeaderPendingChange, setNotDamageHeaderPendingChange] =
    useState<"toFalse" | "toNull" | null>(null);

  // 被害なしヘッダーチェックボックスの状態を計算
  useEffect(() => {
    if (!Array.isArray(disasterStatuses) || disasterStatuses.length === 0) {
      setNotDamageHeaderCheckboxChecked(false);
      return;
    }
    // すべてのデータがnot_damage=falseの場合にチェック
    const allFalse = disasterStatuses.every((status) => status.not_damage === false);
    setNotDamageHeaderCheckboxChecked(allFalse);
  }, [disasterStatuses]);

  // 被害なしヘッダーチェックボックス変更処理
  const handleNotDamageHeaderCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newChecked = e.target.checked;

    if (newChecked) {
      // チェックON時：not_damage=nullのデータがあるか確認
      const hasNullValues = disasterStatuses.some((status) => status.not_damage === null || status.not_damage === undefined);
      if (hasNullValues) {
        setNotDamageHeaderPendingChange('toFalse');
        setNotDamageHeaderPopoverOpen(true);
      } else {
        // nullのデータがない場合は直接チェック状態を変更
        setNotDamageHeaderCheckboxChecked(true);
      }
    } else {
      // チェックOFF時：not_damage=falseのデータがあるか確認
      const hasFalseValues = disasterStatuses.some((status) => status.not_damage === false);
      if (hasFalseValues) {
        setNotDamageHeaderPendingChange('toNull');
        setNotDamageHeaderPopoverOpen(true);
      } else {
        // falseのデータがない場合は直接チェック状態を変更
        setNotDamageHeaderCheckboxChecked(false);
      }
    }
  };

  // 被害なしヘッダーポップオーバー閉じる
  const handleNotDamageHeaderPopoverClose = () => {
    setNotDamageHeaderPopoverOpen(false);
    setNotDamageHeaderPendingChange(null);
  };

  // 被害なしヘッダー一括変更処理
  const handleNotDamageHeaderBatchChange = (isSuccess: boolean) => {
    if (!isSuccess) {
      // 「いいえ」の場合はチェック状態を元に戻す
      const allFalse = disasterStatuses.every((status) => status.not_damage === false);
      setNotDamageHeaderCheckboxChecked(allFalse);
      setNotDamageHeaderPopoverOpen(false);
      setNotDamageHeaderPendingChange(null);
      return;
    }

    // 「はい」の場合：一括変更を実行
    const updatedArray = [...disasterStatuses];

    if (notDamageHeaderPendingChange === 'toFalse') {
      // not_damage=nullをnot_damage=falseに変更
      updatedArray.forEach((status) => {
        if (status.not_damage === null || status.not_damage === undefined) {
          status.not_damage = false;
        }
      });
      setNotDamageHeaderCheckboxChecked(true);
    } else if (notDamageHeaderPendingChange === 'toNull') {
      // not_damage=falseをnot_damage=nullに変更
      updatedArray.forEach((status) => {
        if (status.not_damage === false) {
          status.not_damage = null;
        }
      });
      setNotDamageHeaderCheckboxChecked(false);
    }

    setDisasterStatuses(updatedArray);
    setNotDamageHeaderPopoverOpen(false);
    setNotDamageHeaderPendingChange(null);
  };

  return (
    <>
      {/*{process.env.NODE_ENV === "development" && (*/}
      {/*  <Button onClick={consoleCheck}>コンソール確認</Button>*/}
      {/*)}*/}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          padding: "16px 0 32px",
          "@media screen and (max-width:600px)": {
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setPage(0);
            getDisasterSsData(0);
          }}
          sx={{ width: "208px" }}
          name="searchButton"
        >
          検索
        </Button>
      </Box>
      <Divider />
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            gap: "16px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              paddingLeft: "40px",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索結果
          </Box>
        </Box>
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
          <TablePagination
            rowsPerPageOptions={[10, 30, 50]}
            component="div"
            count={totalData | 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
          <TableContainer>
            <Table
              stickyHeader
              aria-label="sticky table"
              sx={{ width: "100%" }}
            >
              <TableHead>
                <TableRow sx={{ overflowX: "auto" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={`${column.label}_${column.id}`}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        position: column.position,
                        right: column.right,
                        left: column.left,
                        zIndex: column.zIndex,
                      }}
                      sx={{
                        padding: "8px",
                        "@media screen and (max-width:900px)": {
                          display:
                            column.isSmallScreen === true
                              ? "table-cell"
                              : "none",
                        },
                        backgroundColor: column.changeColor
                          ? "primary.light"
                          : "#FFF",
                        color: column.changeColor ? "#FFF" : "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid #606060",
                      }}
                    >
                      {column.id === "not_damage" ? (
                        <Box sx={{ display: "grid", alignItems: "center" }}>

                          <Box>{column.label}</Box>
                          <Box>

                            <Checkbox
                              checked={notDamageHeaderCheckboxChecked}
                              onChange={handleNotDamageHeaderCheckboxChange}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: column.changeColor ? "#FFF" : "outline.dark",
                                "&.Mui-checked": {
                                  color: column.changeColor ? "#FFF" : "primary.main",
                                },
                                pointerEvents: "auto",
                              }}
                            />
                            一括なし
                          </Box>
                        </Box>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {disasterStatuses?.map((disasterstatus, rowNum) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={`${disasterstatus.id}_${disasterstatus.label}`}
                      onClick={() =>
                        handleLocationCodeClick(disasterstatus, rowNum)
                      }
                      name={`searchResult_${rowNum}`}
                      sx={{
                        pointerEvents: "none",
                        "@media screen and (max-width:900px)": {
                          cursor: "pointer",
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      {columns.map((column) => {
                        // const value = disasterstatus[column.id];
                        // editUserカラムの背景色を決定
                        let cellBackgroundColor = "white";
                        if (column.editUser) {
                          const editUser = disasterstatus?.["edit_user"];
                          if (editUser) {
                            const roleId = editUser?.m_organizations?.[0]?.m_role?.id;
                            if (roleId === 7) {
                              cellBackgroundColor = "yellowgreen";
                            } else if (roleId === 8) {
                              cellBackgroundColor = "orange";
                            } else {
                              cellBackgroundColor = "lightgray";
                            }
                          } else {
                            cellBackgroundColor = "white";
                          }
                        }
                        return (
                          <TableCell
                            key={`${column.label}_${column.id}`}
                            align={column.align}
                            style={{
                              minWidth: column.minWidth,
                              position: column.position,
                              right: column.right,
                              left: column.left,
                              backgroundColor: cellBackgroundColor,
                              zIndex: column.zIndex,
                            }}
                            sx={{
                              padding: "8px",
                              "@media screen and (max-width:900px)": {
                                display:
                                  column.isSmallScreen === true
                                    ? "table-cell"
                                    : "none",
                              },
                            }}
                          >
                            {column.checkBox ? (
                              <CheckboxValidate
                                disasterData={disasterstatus}
                                column={column.id}
                                changedValue={(value) =>
                                  handleChangeDisasterStatus(
                                    value,
                                    disasterstatus.id,
                                    column.id
                                  )
                                }
                              />
                            ) : column.textField ? (
                              <StatusesTextfield
                                disasterData={disasterstatus}
                                column={column.id}
                                changedValue={(value) =>
                                  handleChangeDisasterStatus(
                                    value,
                                    disasterstatus.id,
                                    column.id
                                  )
                                }
                              />
                            ) : column.pullDown ? (
                              <SelecterValidate
                                disasterData={disasterstatus}
                                column={column.id}
                                changedValue={(value) =>
                                  handleChangeDisasterStatus(
                                    value,
                                    disasterstatus.id,
                                    column.id
                                  )
                                }
                              />
                            ) : column.defaultSelecter ? (
                              <DefaultSelector
                                column={column.id}
                                value={disasterstatus}
                              />
                            ) : column.site ? (
                              <>{disasterstatus?.["site"]?.[column.id]}</>
                            ) : column.targetUser ? (
                              <TargetUserColumn
                                disasterStatusId={disasterstatus.id}
                                disasterStatusUsers={disasterStatusUsers}
                              />
                            ) : column.editUser ? (
                              <EditUserColumn disasterStatus={disasterstatus} />
                            ) : column.mBranch ? (
                              <>{disasterstatus?.site?.m_branch?.[column.id]}</>
                            ) : column.mCustomer ? (
                              <>
                                {disasterstatus?.site?.m_customer?.[column.id]}
                              </>
                            ) : column.mDivision ? (
                              <>
                                {disasterstatus?.site?.m_division?.[column.id]}
                              </>
                            ) : column.existence ? (
                              <>
                                {existenceValidate(
                                  Boolean(disasterstatus?.[column.id]),
                                )}
                              </>
                            ) : column.id === "latest_alive_at" ||
                              column.id === "latest_sales_at" ||
                              column.id === "damage_at" ||
                              column.id === "edit_at" ? (
                              formatDate(
                                disasterstatus?.[column.id] as string | null,
                              )
                            ) : column.id === "update_user" ? (
                              <>{disasterstatus?.update_user?.name}</>
                            ) : (
                              <>{disasterstatus?.[column.id]}</>
                            )}
                            <ButtonVaridate
                              button={column.button}
                              id={disasterstatus["id"]}
                              detailId={disasterstatus["disaster_detail_id"]}
                              siteId={disasterstatus["site_id"]}
                              kmlId={disasterstatus["disaster_detail_kml_id"]}
                              rowNum={rowNum}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
            <Box
              sx={{
                display: "flex",
                height: "56px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "2px solid #CCC",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  paddingLeft: "8px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                被害状況詳細
              </Box>
              <IconButton
                aria-label="close"
                onClick={handlePopoverClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
              <TableBody>
                {selectedRow &&
                  columns.slice(0, 36).map((column) => {
                    // editUserカラムの背景色を決定
                    let cellBackgroundColor = "white";
                    if (column.editUser) {
                      const editUser = selectedRow?.["edit_user"];
                      if (editUser) {
                        const roleId = editUser?.m_organizations?.[0]?.m_role?.id;
                        if (roleId === 7) {
                          cellBackgroundColor = "yellowgreen";
                        } else if (roleId === 8) {
                          cellBackgroundColor = "orange";
                        } else {
                          cellBackgroundColor = "lightgray";
                        }
                      } else {
                        cellBackgroundColor = "white";
                      }
                    }
                    return (
                      <TableRow
                        role="checkbox"
                        tabIndex={-1}
                        key={`${column.id}_${column.label}`}
                      >
                        <TableCell
                          align={column.align}
                          sx={{
                            color: "outline.dark",
                            fontWeight: "bold",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              WebkitWritingMode: "horizontal-tb",
                              height: "100%",
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={column.align}
                          sx={{
                            writingMode: "horizontal-tb",
                            boxSizing: "border-box",
                            width: "calc((100% - 64px) / 2)",
                            overflowWrap: "break-word",
                            backgroundColor: cellBackgroundColor,
                          }}
                        >
                          {column.button ? (
                            <ButtonVaridate
                              button={true}
                              id={selectedRow?.["id"]}
                              detailId={selectedRow?.["disaster_detail_id"]}
                              siteId={selectedRow?.["site_id"]}
                              kmlId={selectedRow?.["disaster_detail_kml_id"]}
                              rowNum={selectedRowNum === "" ? 0 : selectedRowNum}
                              isDialog={true}
                            />
                          ) : column.checkBox ? (
                            <CheckboxValidate
                              disasterData={selectedRow}
                              column={column.id}
                              changedValue={(value) =>
                                handleChangeDisasterStatus(
                                  value,
                                  selectedRow.id,
                                  column.id
                                )
                              }
                            />
                          ) : column.textField ? (
                            <StatusesTextfield
                              disasterData={selectedRow}
                              column={column.id}
                              changedValue={(value) =>
                                handleChangeDisasterStatus(
                                  value,
                                  selectedRow.id,
                                  column.id
                                )
                              }
                            />
                          ) : column.pullDown ? (
                            <SelecterValidate
                              disasterData={selectedRow}
                              column={column.id}
                              changedValue={(value) =>
                                handleChangeDisasterStatus(
                                  value,
                                  selectedRow.id,
                                  column.id
                                )
                              }
                            />
                          ) : column.defaultSelecter ? (
                            <DefaultSelector
                              column={column.id}
                              value={selectedRow}
                            />
                          ) : column.site ? (
                            <>{selectedRow?.["site"]?.[column.id]}</>
                          ) : column.targetUser ? (
                            <TargetUserColumn
                              disasterStatusId={selectedRow.id}
                              disasterStatusUsers={disasterStatusUsers}
                            />

                          ) : column.editUser ? (
                            <EditUserColumn disasterStatus={selectedRow} />
                          ) : column.mBranch ? (
                            <>{selectedRow?.site?.m_branch?.name}</>
                          ) : column.mCustomer ? (
                            <>{selectedRow?.site?.m_customer?.[column.id]}</>
                          ) : column.mDivision ? (
                            <>{selectedRow?.site?.m_division?.[column.id]}</>
                          ) : column.existence ? (
                            <>
                              {existenceValidate(
                                Boolean(selectedRow?.[column.id]),
                              )}
                            </>
                          ) : column.id === "latest_alive_at" ||
                            column.id === "latest_sales_at" ||
                            column.id === "damage_at" ||
                            column.id === "update_at" ? (
                            formatDate(selectedRow?.[column.id] as string | null)
                          ) : column.id === "update_user" ? (
                            <>{selectedRow?.update_user?.name}</>
                          ) : (
                            <>{selectedRow?.[column.id]}</>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Dialog>
          <SnackbarCustom
            data={snackBarstatus}
            openStatus={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 30, 50]}
            component="div"
            count={totalData | 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
          <PopoverCustom
            open={popoverRegisterOpen}
            onClose={registerPopupClose}
            yesNo={(e) => putrecoberyReportSingle(e)}
            title={"登録確認"}
            contents={"登録してもよろしいですか？"}
          />
          <PopoverCustom
            open={popoverBatchRegisterOpen}
            onClose={batchRegisterPopupClose}
            yesNo={(e) => batchRegister(e)}
            title={"一括登録確認"}
            contents={`${batchRegisterCount}件を一括保存してもよろしいですか？`}
          />
          <PopoverCustom
            open={notDamageHeaderPopoverOpen}
            onClose={handleNotDamageHeaderPopoverClose}
            yesNo={(e) => handleNotDamageHeaderBatchChange(e)}
            title={"一括変更確認"}
            contents={
              notDamageHeaderPendingChange === 'toFalse' ? (
                <>
                  現在表示中の被害「未入力」を「なし」に変更しますがよろしいですか？
                  <br />
                  注:一括保存または保存ボタンを押すまでは保存されません。
                </>
              ) : (
                <>
                  現在表示中の被害「なし」を「未入力」状態に戻しますがよろしいですか？
                  <br />
                  注:一括保存または保存ボタンを押すまでは保存されません。
                </>
              )
            }
          />

        </Paper>
      </Box>
    </>
  );
});

SearchResult.displayName = "SearchResult";

export default SearchResult;
